/**
 * Bouton + panneau "Prévisualiser destinataires" pour Trésorerie.
 * Affiche la liste effective des emails + WhatsApp en distinguant par source (direct / extra / staff).
 *
 * Props :
 *   - form: la config actuelle du modal (finance_team_email, extra_emails, admin_whatsapp, staff_user_ids, etc.)
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Eye, Mail, MessageSquare, Loader2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RecipientsPreview({ form }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/treasury/settings/preview-recipients`, {
        finance_team_email: form.finance_team_email || null,
        treasurer_email: form.treasurer_email || null,
        cfo_email: form.cfo_email || null,
        extra_emails: form.extra_emails || [],
        admin_whatsapp: form.admin_whatsapp || null,
        extra_whatsapps: form.extra_whatsapps || [],
        staff_user_ids: form.staff_user_ids || [],
      });
      setPreview(data);
      setOpen(true);
    } catch (e) {
      toast({ title: 'Erreur preview', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="recipients-preview">
      <button type="button" onClick={fetchPreview} disabled={loading}
              className="inline-flex items-center gap-1.5 text-xs border border-slate-300 bg-white rounded-md px-3 py-1.5 hover:bg-blue-50 disabled:opacity-50"
              data-testid="recipients-preview-btn">
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5 text-blue-600" />}
        Prévisualiser destinataires
      </button>

      {open && preview && (
        <div className="mt-3 border border-slate-200 rounded-lg bg-slate-50 p-3 space-y-3" data-testid="recipients-preview-panel">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-1">
              <Mail className="h-3.5 w-3.5 text-blue-600" />
              Emails ({preview.emails?.total || 0})
            </div>
            {preview.emails?.total === 0 ? (
              <div className="text-[11px] text-rose-600 italic">Aucun email configuré</div>
            ) : (
              <div className="space-y-0.5">
                {preview.emails.sources.direct.map((e, i) => (
                  <div key={`d${i}`} className="flex items-center gap-2 text-[11px]">
                    <span className="inline-block w-14 text-slate-400">direct</span>
                    <span className="text-slate-700">{e}</span>
                  </div>
                ))}
                {preview.emails.sources.extra.map((e, i) => (
                  <div key={`e${i}`} className="flex items-center gap-2 text-[11px]">
                    <span className="inline-block w-14 text-emerald-600">extra</span>
                    <span className="text-slate-700">{e}</span>
                  </div>
                ))}
                {preview.emails.sources.staff.map((e, i) => (
                  <div key={`s${i}`} className="flex items-center gap-2 text-[11px]">
                    <span className="inline-block w-14 text-blue-600">staff</span>
                    <span className="text-slate-700">{e}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 pt-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-1">
              <MessageSquare className="h-3.5 w-3.5 text-emerald-600" />
              WhatsApp ({preview.whatsapp?.total || 0})
            </div>
            {preview.whatsapp?.total === 0 ? (
              <div className="text-[11px] text-rose-600 italic">Aucun numéro configuré</div>
            ) : (
              <div className="space-y-0.5">
                {preview.whatsapp.sources.direct.map((p, i) => (
                  <div key={`d${i}`} className="flex items-center gap-2 text-[11px]">
                    <span className="inline-block w-14 text-slate-400">direct</span>
                    <span className="text-slate-700 font-mono">{p}</span>
                  </div>
                ))}
                {preview.whatsapp.sources.extra.map((p, i) => (
                  <div key={`e${i}`} className="flex items-center gap-2 text-[11px]">
                    <span className="inline-block w-14 text-emerald-600">extra</span>
                    <span className="text-slate-700 font-mono">{p}</span>
                  </div>
                ))}
                {preview.whatsapp.sources.staff.map((p, i) => (
                  <div key={`s${i}`} className="flex items-center gap-2 text-[11px]">
                    <span className="inline-block w-14 text-blue-600">staff</span>
                    <span className="text-slate-700 font-mono">{p}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="text-[10px] text-slate-500 italic pt-1 border-t border-slate-200">
            💡 Les doublons sont automatiquement dédupliqués. Les users staff sans téléphone
            sont ignorés côté WhatsApp mais gardent leur email pour l&apos;envoi.
          </div>
        </div>
      )}
    </div>
  );
}
